import { useMemo } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { Select, Input, Button } from 'antd';
import {
  SECTION_ORDER,
  HISTORY_PERIODS,
  DEFAULT_HISTORY_FILTERS,
} from '@/shared/history';
import { getUserFullName } from '@/utils/utils';

/**
 * HistoryFilters
 *
 * Period, author, section and free text. Author options come from the entries
 * actually present, so the bar never offers a choice that returns nothing.
 */
export default function HistoryFilters({ events, filters, onChange, t }) {
  const userOptions = useMemo(() => {
    const seen = new Map();
    for (const event of events || []) {
      const id = event.user?._id ? String(event.user._id) : 'system';
      if (seen.has(id)) continue;
      seen.set(
        id,
        event.user
          ? getUserFullName(event.user.firstName, event.user.lastName)
          : t('ui.system')
      );
    }
    return [
      { value: 'all', label: t('ui.allUsers') },
      ...[...seen.entries()].map(([value, label]) => ({ value, label })),
    ];
  }, [events, t]);

  const sectionOptions = useMemo(
    () => [
      { value: 'all', label: t('ui.allSections') },
      ...SECTION_ORDER.map((key) => ({ value: key, label: t(`section.${key}`) })),
    ],
    [t]
  );

  const periodOptions = useMemo(
    () =>
      HISTORY_PERIODS.map((option) => ({
        value: option.value,
        label: t(option.labelKey),
      })),
    [t]
  );

  const isDirty = Object.keys(DEFAULT_HISTORY_FILTERS).some(
    (key) => filters[key] !== DEFAULT_HISTORY_FILTERS[key]
  );

  return (
    <div className="flex flex-wrap items-center gap-2 rounded border border-gray-200 bg-gray-50 p-2">
      <Select
        size="small"
        className="min-w-[9rem]"
        value={filters.period}
        onChange={(period) => onChange({ ...filters, period })}
        options={periodOptions}
      />

      <Select
        size="small"
        className="min-w-[10rem]"
        value={filters.user}
        onChange={(user) => onChange({ ...filters, user })}
        options={userOptions}
      />

      <Select
        size="small"
        className="min-w-[10rem]"
        value={filters.section}
        onChange={(section) => onChange({ ...filters, section })}
        options={sectionOptions}
      />

      <Input
        size="small"
        allowClear
        className="min-w-[12rem] max-w-xs flex-1"
        prefix={<SearchOutlined className="text-gray-400" />}
        placeholder={t('ui.search')}
        value={filters.query}
        onChange={(event) => onChange({ ...filters, query: event.target.value })}
      />

      {isDirty && (
        <Button
          size="small"
          type="text"
          onClick={() => onChange({ ...DEFAULT_HISTORY_FILTERS })}
        >
          {t('ui.reset')}
        </Button>
      )}
    </div>
  );
}
